import { useMutation, useQueryClient } from '@tanstack/react-query'
import type { QueryClient, QueryKey } from '@tanstack/react-query'
import { useTranslation } from 'react-i18next'
import { eventsApi } from '@/api/endpoints'
import { queryKeys } from '@/config/queryConfig'
import { isOpenCritical } from '@/lib/criticalEvents'
import { toast } from '@/lib/toast'
import { useAuthStore } from '@/store/authStore'
import type { Event, User } from '@/types'

// #294 — acknowledge critical events, optimistically: every cached events list (the open-critical list,
// the dashboard's recent rows, the Events page) shows the event acknowledged by this user at once — the
// pinned red row turns grey and goes back to time order, the alert bar, popups, tab title and sound stop
// when none is left — and the old state comes back if the server refuses. Same pattern as
// useMarkEventRead (#292). Then the open list is refetched; the server's `critical_acknowledged` message
// refreshes the rest, here and in every other open dashboard.
// #294 redesign (CP 294 edge 1) — "Acknowledge all" sends `up_to`: the created_at of the newest open alert
// loaded, exactly as the API wrote it (never the PC clock), and the API acknowledges every open alert the
// user can see created at or before it — also those beyond the 50 loaded; one that arrives during the
// click stays open. An old API (400) or no created_at to send (only WebSocket placeholders loaded): the
// ids shown, as before.

interface EventPage { results: Event[]; [k: string]: unknown }
interface InfiniteEvents { pages: EventPage[]; [k: string]: unknown }
type Snapshot = Array<[QueryKey, unknown]>

const isPage = (v: unknown): v is EventPage =>
  !!v && typeof v === 'object' && Array.isArray((v as { results?: unknown }).results)
const isInfinite = (v: unknown): v is InfiniteEvents =>
  !!v && typeof v === 'object' && Array.isArray((v as { pages?: unknown }).pages)

/** Which who / when fields a team-wide tick writes: Acknowledge's, or (#406) Seen's. */
type TickFields = { by: 'acknowledged_by'; at: 'acknowledged_at' } | { by: 'seen_by'; at: 'seen_at' }
const ACKNOWLEDGED: TickFields = { by: 'acknowledged_by', at: 'acknowledged_at' }

function markAcknowledged(queryClient: QueryClient, match: (e: Event) => boolean, by: string | null, at: string, fields: TickFields) {
  const mark = (e: Event): Event => (!e[fields.at] && match(e) ? { ...e, [fields.by]: by, [fields.at]: at } : e)
  const mapPage = (p: EventPage): EventPage => ({ ...p, results: p.results.map(mark) })
  queryClient.setQueriesData({ queryKey: ['events'] }, (old: unknown) => {
    if (isInfinite(old)) return { ...old, pages: old.pages.map(mapPage) }
    if (isPage(old)) return mapPage(old)
    return old
  })
}

/** Order two created_at strings from the API: by time, then (the same millisecond) by the string itself,
 *  which keeps the microseconds the API writes in one fixed format. */
export function compareCreatedAt(a: string, b: string): number {
  const d = Date.parse(a) - Date.parse(b)
  if (d !== 0) return d
  return a < b ? -1 : a > b ? 1 : 0
}

const hasCreatedAt = (e: Event): boolean =>
  !e._seeded && typeof e.created_at === 'string' && !Number.isNaN(Date.parse(e.created_at))

/** The created_at string of the newest loaded open alert, as the API wrote it; null when none has one
 *  (WebSocket placeholders carry none). */
export function newestCreatedAt(open: Event[]): string | null {
  let newest: string | null = null
  for (const e of open) {
    if (!hasCreatedAt(e)) continue
    if (newest === null || compareCreatedAt(e.created_at, newest) > 0) newest = e.created_at
  }
  return newest
}

const httpStatus = (err: unknown): number | undefined =>
  (err as { response?: { status?: number } } | null)?.response?.status

async function snapshot(queryClient: QueryClient): Promise<Snapshot> {
  await queryClient.cancelQueries({ queryKey: ['events'] })
  return queryClient.getQueriesData({ queryKey: ['events'] })
}

function restore(queryClient: QueryClient, prev: Snapshot | undefined) {
  prev?.forEach(([key, data]) => queryClient.setQueryData(key, data))
}

/** The server's rule for the name: full name, else the username. */
function displayName(user: Pick<User, 'username' | 'first_name' | 'last_name'> | null | undefined): string | null {
  if (!user) return null
  return `${user.first_name ?? ''} ${user.last_name ?? ''}`.trim() || user.username || null
}

// #406 — Seen on a suspicious event is the same tick with other fields: `options` names them and the open list to
// refresh when it settles. By default Acknowledge's, as before.
export function useAcknowledgeMutation<V, R = unknown>(
  send: (v: V) => Promise<R>,
  matchOf: (v: V) => (e: Event) => boolean,
  failedKey: 'acknowledgeFailed' | 'bar.acknowledgeAllFailed' | 'seenFailed',
  alsoOnMutate?: (queryClient: QueryClient, v: V) => void,
  options: { fields?: TickFields; settleKey?: QueryKey } = {},
) {
  const fields = options.fields ?? ACKNOWLEDGED
  const settleKey = options.settleKey ?? queryKeys.events.criticalOpen()
  const queryClient = useQueryClient()
  const { t } = useTranslation('common')
  const user = useAuthStore((s) => s.user)
  return useMutation({
    mutationFn: send,
    onMutate: async (v: V) => {
      const prev = await snapshot(queryClient)
      markAcknowledged(queryClient, matchOf(v), displayName(user), new Date().toISOString(), fields)
      alsoOnMutate?.(queryClient, v)
      return { prev }
    },
    onError: (_err, _v, ctx) => {
      restore(queryClient, ctx?.prev)
      // #406 — the restore puts back every events list as it was at the click, so an alert the WebSocket seeded while
      // the call was on its way is gone from it: refetch both open lists, whichever tick failed.
      queryClient.invalidateQueries({ queryKey: queryKeys.events.criticalOpen() })
      queryClient.invalidateQueries({ queryKey: queryKeys.events.suspiciousOpen() })
      toast.error(failedKey === 'seenFailed' ? t('suspicious.seenFailed') : t(`critical.${failedKey}`))
      // the restored snapshot may be older than the server (another user acknowledged meanwhile)
      queryClient.invalidateQueries({ queryKey: queryKeys.events.recent() })
      queryClient.invalidateQueries({ queryKey: ['events', 'paginated'] })
    },
    onSettled: () => {
      queryClient.invalidateQueries({ queryKey: settleKey })
    },
  })
}

/** Acknowledge one critical event. */
export function useAcknowledgeCritical() {
  return useAcknowledgeMutation((id: number) => eventsApi.acknowledge(id), (id) => (e) => e.id === id, 'acknowledgeFailed')
}

/**
 * "Acknowledge all" (the alert bar): `open` = the open critical alerts loaded (the open list, newest
 * first). Sends `up_to` = the newest one's created_at (see the file comment), else the ids shown (up to
 * 200); an old API that refuses `up_to` (400) gets the ids. Optimistic: every loaded open alert created at
 * or before `up_to` (all of them but WebSocket placeholders) turns acknowledged in every cached list, and
 * the open list's count drops to what stays open, so the bar and the tab title clear at once.
 */
export function useAcknowledgeAllCritical() {
  return useAcknowledgeMutation(
    async (open: Event[]) => {
      const ids = open.map((e) => e.id).slice(0, 200)
      const upTo = newestCreatedAt(open)
      if (upTo === null) return eventsApi.acknowledgeAll(ids)
      try {
        return await eventsApi.acknowledgeAllUpTo(upTo)
      } catch (err) {
        if (httpStatus(err) === 400 && ids.length > 0) return eventsApi.acknowledgeAll(ids)
        throw err
      }
    },
    (open) => {
      const upTo = newestCreatedAt(open)
      if (upTo === null) {
        const ids = new Set(open.map((e) => e.id))
        return (e) => ids.has(e.id)
      }
      return (e) => isOpenCritical(e) && hasCreatedAt(e) && compareCreatedAt(e.created_at, upTo) <= 0
    },
    'bar.acknowledgeAllFailed',
    (queryClient, open) => {
      if (newestCreatedAt(open) === null) return
      // The server acknowledges the open alerts beyond the loaded page too (all older than up_to): the
      // count left open is what this page still shows open (its placeholders).
      queryClient.setQueryData<EventPage>(queryKeys.events.criticalOpen(), (old) =>
        isPage(old) ? { ...old, count: old.results.length } : old)
    },
  )
}
